// src/services/incidentApi.js
// Cliente del módulo Hechos Operativos. Aislado del resto a propósito: no
// comparte endpoints ni tipos con tickets, chat o Atención a Clientes.
import api from './api';

// Arranque de la pantalla: perfil dentro del módulo, catálogo de tipos de
// evento y destinatarios fijos del protocolo.
export const bootstrap = () => api.get('/incidents/bootstrap').then(r => r.data);

// Registro (bandeja). El servidor recorta según el perfil.
export const listIncidents = (params = {}) =>
    api.get('/incidents', { params }).then(r => r.data);

// Expediente completo + entradas del timeline (ya filtradas por visibilidad).
export const getIncident = (id) => api.get(`/incidents/${id}`).then(r => r.data);

// Rama "a un colaborador" del árbol: ficha del intranet por número de empleado.
export const lookupEmployee = (idEmpleado) =>
    api.get(`/incidents/employee/${idEmpleado}`).then(r => r.data);

export const createIncident = (payload) =>
    api.post('/incidents', payload).then(r => r.data);

// Escribir en el expediente. visibility: 'publica' (hilo) | 'legal' (bitácora).
export const addEntry = (id, body, visibility = 'publica') =>
    api.post(`/incidents/${id}/entries`, { body, visibility }).then(r => r.data);

// IA: devuelve una propuesta, nunca algo oficial.
export const structureNarrative = (id) =>
    api.post(`/incidents/${id}/estructurar`).then(r => r.data);

// Validación del abogado: el único punto donde algo se vuelve oficial.
export const validateNarrative = (id, { texto, eventType }) =>
    api.post(`/incidents/${id}/validar`, { texto, eventType }).then(r => r.data);

export const advanceStage = (id, etapa) =>
    api.patch(`/incidents/${id}/etapa`, { etapa }).then(r => r.data);

// Reasignación manual del titular (supervisión legal o el propio titular).
export const listLawyers = () => api.get('/incidents/lawyers').then(r => r.data);
export const assignLawyer = (id, lawyerId) =>
    api.patch(`/incidents/${id}/abogado`, { lawyerId }).then(r => r.data);

// Cierre formal con resultado, y eliminación definitiva (solo admin).
export const closeIncident = (id, { resultado, nota }) =>
    api.post(`/incidents/${id}/cerrar`, { resultado, nota }).then(r => r.data);
export const deleteIncident = (id) =>
    api.delete(`/incidents/${id}`).then(r => r.data);

// Etiquetas de resultado de cierre, compartidas por expediente y reportes.
export const RESULTADOS_CIERRE = [
    { key: 'recuperado',          label: 'Recuperado' },
    { key: 'denuncia_presentada', label: 'Denuncia presentada' },
    { key: 'rescision',           label: 'Rescisión' },
    { key: 'sin_accion',          label: 'Sin acción' },
    { key: 'seguro',              label: 'Cubierto por seguro' },
    { key: 'otro',                label: 'Otro' },
];

// Equipo del caso: co-abogados que trabajan el expediente junto al titular.
export const addCoLawyer = (id, lawyerId) =>
    api.post(`/incidents/${id}/co-abogados`, { lawyerId }).then(r => r.data);
export const removeCoLawyer = (id, lawyerId) =>
    api.delete(`/incidents/${id}/co-abogados/${lawyerId}`).then(r => r.data);

export const resolvePending = (id, entryId) =>
    api.patch(`/incidents/${id}/pendientes/${entryId}`).then(r => r.data);

// Evidencia. `archivos` es un FileList o array de File.
export const uploadAttachments = (id, archivos, docType) => {
    const fd = new FormData();
    [...archivos].forEach(f => fd.append('archivos', f));
    if (docType) fd.append('docType', docType);
    return api.post(`/incidents/${id}/adjuntos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
};

export const getAttachmentUrl = (id, idx) =>
    api.get(`/incidents/${id}/adjuntos/${idx}`).then(r => r.data);

export const getPdf        = (id) => api.get(`/incidents/${id}/pdf`).then(r => r.data);
export const regeneratePdf = (id) => api.post(`/incidents/${id}/pdf`).then(r => r.data);

// ── Etiquetas compartidas por las tres pantallas ─────────────────────────
export const ETAPAS = [
    { key: 'recibido',      label: 'Reporte recibido' },
    { key: 'asignado',      label: 'Abogado asignado' },
    { key: 'revisado',      label: 'Información revisada' },
    { key: 'documentacion', label: 'Documentación completa' },
    { key: 'gestion_legal', label: 'En gestión legal' },
    { key: 'concluido',     label: 'Concluido' },
];
export const etapaLabel = (k) => ETAPAS.find(e => e.key === k)?.label || k;
export const etapaIndex = (k) => ETAPAS.findIndex(e => e.key === k);

// ── Mapa de riesgo y patrones ───────────────────────────────────────────
// Todo sale de datos que el formato ya pedía; no agrega captura a nadie.
export const getRiesgo = (params = {}) =>
    api.get('/incidents/analytics/riesgo', { params }).then(r => r.data);

export const getReincidencias = (params = {}) =>
    api.get('/incidents/analytics/reincidencias', { params }).then(r => r.data);

// Casos parecidos a uno dado, cada uno con la razón de la coincidencia.
export const getSimilares = (id) =>
    api.get(`/incidents/${id}/similares`).then(r => r.data);

// Reportería de gestión: cómo se están atendiendo los casos.
export const getGestion = (params = {}) =>
    api.get('/incidents/analytics/gestion', { params }).then(r => r.data);

/**
 * Descarga el registro en CSV. Va por axios (no window.open) porque el endpoint
 * exige el token del encabezado; con una pestaña nueva el navegador no lo manda
 * y la descarga saldría 401.
 */
// Marcas y sucursales disponibles para filtrar el Reporte HO.
export const exportFiltros = () =>
    api.get('/incidents/analytics/export-filtros').then(r => r.data);

/**
 * Descarga el "REPORTES HO" en Excel — el formato de 17 columnas que legal
 * llenaba a mano. Va por axios por la misma razón que el CSV: el token.
 * params: { desde, hasta, marca, sucursal } (todos opcionales).
 */
export const exportarReporteHO = async (params = {}) => {
    const r = await api.get('/incidents/analytics/export-ho', { params, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `REPORTES-HO-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

export const exportarCsv = async (params = {}) => {
    const r = await api.get('/incidents/analytics/export', { params, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `hechos-operativos-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

// ── Administración del módulo (solo admin) ──────────────────────────────
export const getAdminConfig = () => api.get('/incidents/admin/config').then(r => r.data);
export const searchUsers    = (q) => api.get('/incidents/admin/users', { params: { q } }).then(r => r.data);
export const updatePolicy   = (patch) => api.patch('/incidents/admin/policy', patch).then(r => r.data);
export const setLawyer      = (userId, body) => api.post(`/incidents/admin/lawyers/${userId}`, body).then(r => r.data);
export const updateZone     = (id, patch) => api.patch(`/incidents/admin/zones/${id}`, patch).then(r => r.data);
export const updateEventType = (id, patch) => api.patch(`/incidents/admin/event-types/${id}`, patch).then(r => r.data);
